import React from "react";
import VerticalAlignTopIcon from "@mui/icons-material/VerticalAlignTop";
import VerticalAlignCenterIcon from "@mui/icons-material/VerticalAlignCenter";
import VerticalAlignBottomIcon from "@mui/icons-material/VerticalAlignBottom";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";

import { uploadLearningFile } from "./swipeDeckHelpers";
import {
  RichTextField,
  RichTextToolbarButton,
  EmojiPickerButton,
} from "./RichTextField";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import { MediaSuggestionBanner } from "./SwipeCardMediaSuggestion";
import MediaLibraryModal from "./MediaLibraryModal";

// Relative size steps. Medium is each layout's existing baseline, so saved
// cards are unchanged; Small/Large step down/up from there. The renderer
// (ImageTextCard) owns the actual per-layout Tailwind size — this is just
// the stored choice.
const TEXT_SIZES = [
  { value: "small", label: "Small", short: "S" },
  { value: "medium", label: "Medium", short: "M" },
  { value: "large", label: "Large", short: "L" },
];

const LAYOUTS = [
  { value: "full_bleed", label: "Full bleed", description: "Image fills card, text overlay" },
  { value: "split", label: "Split", description: "Image top, text below" },
  { value: "text_only", label: "Text only", description: "No image, big typography" },
];

const TEXT_POSITIONS = [
  { value: "top", label: "Top", Icon: VerticalAlignTopIcon },
  { value: "middle", label: "Middle", Icon: VerticalAlignCenterIcon },
  { value: "bottom", label: "Bottom", Icon: VerticalAlignBottomIcon },
];

const TEXT_ALIGNMENTS = [
  { value: "left", label: "Left", Icon: FormatAlignLeftIcon },
  { value: "center", label: "Center", Icon: FormatAlignCenterIcon },
  { value: "right", label: "Right", Icon: FormatAlignRightIcon },
];

const QUICK_EMOJIS = [
  "💡", "🎉", "🎊", "🥳", "🏆", "🎁", "⭐", "❓", "🔥", "✅", "🎯", "🚀",
  "💪", "👏", "🤝", "💯", "❤️", "👀", "📣", "🚨", "⏰", "☕", "🍕", "🍔", "🍩", "🥤",
];

/**
 * Right-pane editor for image+text cards. Controlled by the parent modal:
 * receives `card` and calls `onChange(updatedCard)` on every edit. No
 * internal save state — the parent batches everything and posts on Save.
 */
export function SwipeCardImageTextEditor({ card, onChange, companyId, onGenerateImage, isGeneratingImage }) {
  const [libraryOpen, setLibraryOpen] = React.useState(false);
  const content = card.content_json || {};
  const layout = card.layout_variant || "full_bleed";
  const headline = content.headline || "";
  const body = content.body || "";
  const emoji = content.emoji || null;
  // Layout-aware defaults so existing cards don't shift appearance:
  //   - full_bleed defaults to bottom + left (overlay text at bottom)
  //   - text_only defaults to middle + center (centered big typography)
  //   - split has fixed positioning (no text_position needed)
  const textPosition =
    content.text_position || (layout === "text_only" ? "middle" : "bottom");
  const textAlign =
    content.text_align || (layout === "text_only" ? "center" : "left");
  const headlineSize = content.headline_size || "medium";
  const bodySize = content.body_size || "medium";
  // Headline renders bold by default today, so an undefined flag means bold;
  // italic defaults off. Both are whole-headline toggles (the headline is a
  // plain input, not rich text).
  const headlineBold = content.headline_bold !== false;
  const headlineItalic = content.headline_italic === true;
  const mediaUrl = card.media_url || null;

  const [uploading, setUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState(null);
  const headlineInputRef = React.useRef(null);

  const updateContent = (patch) => {
    onChange({
      ...card,
      content_json: { ...content, ...patch },
    });
  };

  // Insert an emoji into the headline at the caret (or end, if it was never
  // focused). selectionStart/End survive the input losing focus to the
  // popover, so the position read here is the last caret the author placed.
  // We restore the caret just past the inserted emoji after the controlled
  // re-render.
  const insertEmojiIntoHeadline = (emoji) => {
    const input = headlineInputRef.current;
    const start = input?.selectionStart ?? headline.length;
    const end = input?.selectionEnd ?? headline.length;
    updateContent({ headline: headline.slice(0, start) + emoji + headline.slice(end) });
    requestAnimationFrame(() => {
      if (!input) return;
      const pos = start + emoji.length;
      input.focus();
      input.setSelectionRange(pos, pos);
    });
  };

  const handleImagePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setUploadError("Please choose an image file.");
      return;
    }
    setUploading(true);
    setUploadError(null);
    try {
      const { uuid, url } = await uploadLearningFile(companyId, file);
      onChange({
        ...card,
        media_file_uuid: uuid,
        media_url: url,
      });
    } catch (err) {
      setUploadError(err?.message || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleClearImage = () => {
    onChange({ ...card, media_file_uuid: null, media_url: null });
  };

  const showImageSection = layout !== "text_only";

  return (
    <div className="flex flex-col gap-5 p-5">
      {/* Layout variant */}
      <div>
        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">
          Layout
        </label>
        <div className="grid grid-cols-3 gap-2">
          {LAYOUTS.map((opt) => {
            const active = layout === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onChange({ ...card, layout_variant: opt.value })}
                className={[
                  "rounded-lg p-2 text-xs text-center border-2 transition-colors",
                  active
                    ? "border-violet-600 bg-violet-50 text-violet-700"
                    : "border-slate-200 hover:border-slate-300 text-slate-700",
                ].join(" ")}
              >
                <div className="font-semibold">{opt.label}</div>
                <div className="text-[10px] text-slate-500 mt-0.5 leading-tight">
                  {opt.description}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Image upload (hidden for text_only) */}
      {showImageSection && (
        <div>
          <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">
            Image
          </label>
          <MediaSuggestionBanner
            card={card}
            onGenerate={onGenerateImage}
            generating={isGeneratingImage}
          />
          {mediaUrl ? (
            <div className="relative rounded-lg overflow-hidden border border-slate-200 bg-black">
              <img
                src={mediaUrl}
                alt=""
                className="w-full h-40 object-cover"
              />
              <button
                type="button"
                onClick={handleClearImage}
                className="absolute top-2 right-2 px-2 py-1 rounded-md bg-black/70 text-white text-xs hover:bg-black/90"
              >
                Remove
              </button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center h-32 rounded-lg border-2 border-dashed border-slate-300 hover:border-violet-500 cursor-pointer transition-colors text-sm text-slate-500">
              <span>{uploading ? "Uploading…" : "Click to upload an image"}</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleImagePick}
                disabled={uploading}
                className="hidden"
              />
            </label>
          )}
          {/* Library picker — reuse an existing upload, an AI-generated
              image, or a photo extracted from the company's documents,
              instead of uploading the same file again. */}
          <button
            type="button"
            onClick={() => setLibraryOpen(true)}
            disabled={uploading}
            className="mt-2 w-full px-3 py-1.5 text-xs font-medium text-violet-700 border border-violet-200 rounded-lg hover:bg-violet-50 transition-colors inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
          >
            <CollectionsOutlinedIcon style={{ fontSize: 14 }} />
            {mediaUrl ? "Replace from library" : "Choose from library"}
          </button>
          {uploadError && (
            <div className="mt-2 text-xs text-red-600">{uploadError}</div>
          )}
          <MediaLibraryModal
            open={libraryOpen}
            onClose={() => setLibraryOpen(false)}
            companyId={companyId}
            onSelect={({ uuid, url }) =>
              onChange({
                ...card,
                media_file_uuid: uuid,
                media_url: url,
              })
            }
          />
        </div>
      )}

      {/* Emoji prefix — a single large emoji shown above the headline. Uses
          the same popover as the inline emoji inserter so it stays compact. */}
      <div>
        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">
          Emoji prefix (optional)
        </label>
        <div className="flex items-center gap-2">
          <EmojiPickerButton
            emojis={QUICK_EMOJIS}
            selected={emoji}
            onPick={(e) => updateContent({ emoji: e })}
            onClear={() => updateContent({ emoji: null })}
            ariaLabel="Choose emoji prefix"
          />
          <span className="text-xs text-slate-400">
            {emoji ? "Shown large above the headline" : "None"}
          </span>
        </div>
      </div>

      {/* Headline — same toolbar treatment as the body, minus list options
          (a headline is a single line, so bullets/numbers don't apply). The
          bold/italic toggles are whole-headline since it's a plain input,
          not rich text. */}
      <div>
        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">
          Headline
        </label>
        <div className="rounded-lg border border-slate-200 bg-white focus-within:border-violet-500 focus-within:ring-2 focus-within:ring-violet-100">
          <div className="flex items-center gap-0.5 border-b border-slate-200 px-2 py-1">
            <RichTextToolbarButton
              onClick={() => updateContent({ headline_bold: !headlineBold })}
              active={headlineBold}
              aria-label="Bold headline"
            >
              <FormatBoldIcon style={{ fontSize: 16 }} />
            </RichTextToolbarButton>
            <RichTextToolbarButton
              onClick={() => updateContent({ headline_italic: !headlineItalic })}
              active={headlineItalic}
              aria-label="Italic headline"
            >
              <FormatItalicIcon style={{ fontSize: 16 }} />
            </RichTextToolbarButton>
            <EmojiPickerButton
              emojis={QUICK_EMOJIS}
              onPick={insertEmojiIntoHeadline}
            />
            <div className="ml-auto flex items-center">
              <SizePicker
                value={headlineSize}
                onSelect={(v) => updateContent({ headline_size: v })}
              />
            </div>
          </div>
          <input
            ref={headlineInputRef}
            type="text"
            value={headline}
            onChange={(e) => updateContent({ headline: e.target.value })}
            placeholder="A punchy line that frames the card"
            // Reflect the whole-field bold/italic toggles in the input itself
            // so it's WYSIWYG like the body editor — clicking B/I visibly
            // changes the text instead of silently flipping a flag.
            className={[
              "w-full px-3 py-2 bg-transparent rounded-b-lg focus:outline-none text-sm",
              headlineBold ? "font-bold" : "font-normal",
              headlineItalic ? "italic" : "",
            ].join(" ")}
          />
        </div>
      </div>

      {/* Body — rich text via Tiptap */}
      <div>
        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">
          Body (optional)
        </label>
        <RichTextField
          value={body}
          onChange={(html) => updateContent({ body: html })}
          placeholder="Supporting detail. Keep it tight."
          emojiOptions={QUICK_EMOJIS}
          toolbarTrailing={
            <SizePicker
              value={bodySize}
              onSelect={(v) => updateContent({ body_size: v })}
            />
          }
        />
      </div>

      {/* Text alignment — applies to headline + body together. Card-level
          rather than per-paragraph because the Tiptap text-align extension
          isn't in our deps and per-paragraph alignment is rarely the
          actual intent ("center this card's text" usually means the
          whole card, not per-line). */}
      <div>
        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">
          Text alignment
        </label>
        <div className="grid grid-cols-3 gap-2">
          {TEXT_ALIGNMENTS.map((opt) => {
            const active = textAlign === opt.value;
            const Icon = opt.Icon;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => updateContent({ text_align: opt.value })}
                className={[
                  "rounded-lg p-2 text-xs text-center border-2 transition-colors flex flex-col items-center gap-1",
                  active
                    ? "border-violet-600 bg-violet-50 text-violet-700"
                    : "border-slate-200 hover:border-slate-300 text-slate-700",
                ].join(" ")}
              >
                <Icon style={{ fontSize: 18 }} />
                <span className="font-semibold">{opt.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Text position — meaningful for full_bleed and text_only. Split
          has fixed positioning (text always at top of the bottom half). */}
      {(layout === "full_bleed" || layout === "text_only") && (
        <div>
          <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">
            Text position
          </label>
          <div className="grid grid-cols-3 gap-2">
            {TEXT_POSITIONS.map((opt) => {
              const active = textPosition === opt.value;
              const Icon = opt.Icon;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => updateContent({ text_position: opt.value })}
                  className={[
                    "rounded-lg p-2 text-xs text-center border-2 transition-colors flex flex-col items-center gap-1",
                    active
                      ? "border-violet-600 bg-violet-50 text-violet-700"
                      : "border-slate-200 hover:border-slate-300 text-slate-700",
                  ].join(" ")}
                >
                  <Icon style={{ fontSize: 18 }} />
                  <span className="font-semibold">{opt.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/** S / M / L relative-size toggles, styled to match the B/I toolbar buttons. */
function SizePicker({ value, onSelect }) {
  return (
    <div className="flex items-center gap-0.5">
      {TEXT_SIZES.map((opt) => (
        <RichTextToolbarButton
          key={opt.value}
          onClick={() => onSelect(opt.value)}
          active={value === opt.value}
          aria-label={`${opt.label} text size`}
        >
          <span className="text-xs font-semibold">{opt.short}</span>
        </RichTextToolbarButton>
      ))}
    </div>
  );
}
